class FilterBuilder {
  constructor() {
    this.filters = [];
    this.paging = { start: 0, length: 25 };
    this.orders = {};
    this.joins = [];
    this._filterItems = [];
    this._customFilter = null;
    this._customJoin = null;
  }

  where(operatorSymbol, name, value, operator = 'AND', value1 = null, tableName = null) {
    if (!name) return this;
    const filter = {
      logic_operator: operatorSymbol,
      name,
      operator,
      value: value?.toString() ?? null,
      ...(tableName != null ? { table_name: tableName } : {}),
      ...(value1 != null ? { value1 } : {})
    };

    this.filters.push(filter);
    return this;
  }

  equal(name, value, operator = 'AND', tableName = null) {
    return this.where('=', name, value, operator, null, tableName);
  }

  notEqual(name, value, operator = 'AND', tableName = null) {
    return this.where('<>', name, value, operator, null, tableName);
  }

  isNull(name, value, operator = 'AND', tableName = null) {
    return this.where('IS NULL', name, value, operator, null, tableName);
  }

  isNotNull(name, value, operator = 'AND', tableName = null) {
    return this.where('IS NOT NULL', name, value, operator, null, tableName);
  }

  in(name, value, operator = 'AND', tableName = null) {
    return this.where('in', name, value, operator, null, tableName);
  }

  notIn(name, value, operator = 'AND', tableName = null) {
    return this.where('not in', name, value, operator, null, tableName);
  }

  like(name, value, operator = 'AND', tableName = null) {
    return this.where('like', name, value, operator, null, tableName);
  }

  range(name, value, value1, operator = 'AND', tableName = null) {
    return this.where('RANGE', name, value, operator, value1, tableName);
  }

  setPaging(start, length) {
    this.paging = { start, length };
    return this;
  }

  setOrder(columns, ascending = true) {
    this.orders = { columns, ascending };
    return this;
  }

  addJoin(name, columnResults = []) {
    this.joins.push({ name, column_results: columnResults });
    return this;
  }

  /**
   * Add an entry to the separate 'filters' array (used by some endpoints for OR-based quick search).
   * Each filter item: { name, value, table_name? }
   */
  addFilter(name, value, tableName = null) {
    const item = { name, value: value?.toString() ?? '' };
    if (tableName != null) item.table_name = tableName;
    this._filterItems.push(item);
    return this;
  }

  /**
   * Set a raw custom SQL filter string appended by the backend.
   */
  setCustomFilter(customFilter = '') {
    this._customFilter = customFilter;
    return this;
  }

  /**
   * Set a raw custom SQL join string appended by the backend.
   */
  setCustomJoin(customJoin = '') {
    this._customJoin = customJoin;
    return this;
  }

  filterByCompany() {
    try {
      const stored = localStorage.getItem('companyId');

      const companyId = stored == null || stored === '' ? null : isNaN(Number(stored)) ? stored : Number(stored);
      if (companyId != null) {
        this.equal('company_id', companyId);
      }
    } catch (err) {
      console.warn('FilterBuilder.filterByCompany localStorage read failed', err);
    }
    return this;
  }

  clone() {
    const fb = new FilterBuilder();
    fb.filters = JSON.parse(JSON.stringify(this.filters));
    fb.paging = { ...this.paging };
    fb.orders = { ...this.orders };
    fb.joins = JSON.parse(JSON.stringify(this.joins));
    fb._filterItems = JSON.parse(JSON.stringify(this._filterItems));
    fb._customFilter = this._customFilter;
    fb._customJoin = this._customJoin;
    return fb;
  }

  build() {
    let filters = [...this.filters];
    const idx = filters.findIndex((f) => f.name === 'company_id');
    if (idx > 0) {
      const [companyFilter] = filters.splice(idx, 1);
      filters.unshift(companyFilter);
    }

    const result = { paging: this.paging };

    if (filters.length > 0) {
      result.columns = filters;
    }

    if (Object.keys(this.orders).length > 0) {
      result.orders = this.orders;
    }

    if (this.joins.length > 0) {
      result.joins = this.joins;
    }

    if (this._filterItems.length > 0) {
      result.filters = this._filterItems;
    }

    if (this._customFilter != null) {
      result.customFilter = this._customFilter;
    }

    if (this._customJoin != null) {
      result.customJoin = this._customJoin;
    }

    return result;
  }
}

export default FilterBuilder;
